import type { PresenceLease, SystemStatus, TopologySnapshot } from '../../api/types';
import { leaseExpiry, leaseState, type LeaseState } from '../../lib';
import type { LiveAgentView, LiveState } from '../live/agent-state';
import type { TerminalTarget } from './api';

export interface FleetAgent {
  id: string;
  tenantId: string;
  alias: string;
  roomIds: string[];
  roomMembership: Record<string, boolean | undefined>;
  membershipEnabled?: boolean;
  presence?: PresenceLease;
  leaseState: LeaseState;
}

interface MutableFleetAgent {
  tenantId: string;
  alias: string;
  roomIds: Set<string>;
  roomMembership: Map<string, boolean | undefined>;
  membershipStates: boolean[];
  presence?: PresenceLease;
}

export function fleetAgentId(tenantId: string, alias: string): string {
  // Identity keys preserve canonical wire values case-sensitively.
  return `${tenantId}:${alias}`;
}

export function tenantVisibleName(topology: TopologySnapshot | undefined, tenantId: string): string {
  const label = topology?.tenants?.find((tenant) => tenant.id === tenantId)?.label?.trim();
  return label && label.length > 0 ? label : tenantId;
}

/** Combines server topology and observed leases without inventing fleet members. */
export function buildFleetAgents(status?: SystemStatus, topology?: TopologySnapshot): FleetAgent[] {
  const records = new Map<string, MutableFleetAgent>();
  // A stale presence lease must not resurrect a membership the canonical registry explicitly
  // says is not an agent. The identity is tenant-qualified: aliases may repeat across tenants.
  const explicitlyNotAgents = new Set<string>();

  for (const tenant of topology?.tenants ?? []) {
    if (!tenant.id) continue;
    for (const room of tenant.rooms ?? []) {
      for (const member of room.members ?? []) {
        if (!member.alias) continue;
        const id = fleetAgentId(tenant.id, member.alias);
        // A person's seat (the company owner) is never an agent either.
        if (member.registered === false || member.person === true) {
          explicitlyNotAgents.add(id);
          records.delete(id);
          continue;
        }
        const record = records.get(id) ?? {
          tenantId: tenant.id,
          alias: member.alias,
          roomIds: new Set<string>(),
          roomMembership: new Map<string, boolean | undefined>(),
          membershipStates: [],
        };
        if (room.id) {
          record.roomIds.add(room.id);
          record.roomMembership.set(room.id, member.enabled ?? record.roomMembership.get(room.id));
        }
        if (typeof member.enabled === 'boolean') record.membershipStates.push(member.enabled);
        records.set(id, record);
      }
    }
  }

  for (const presence of status?.presence ?? []) {
    if (!presence.tenant_id || !presence.alias) continue;
    const id = fleetAgentId(presence.tenant_id, presence.alias);
    if (explicitlyNotAgents.has(id)) continue;
    const record = records.get(id) ?? {
      tenantId: presence.tenant_id,
      alias: presence.alias,
      roomIds: new Set<string>(),
      roomMembership: new Map<string, boolean | undefined>(),
      membershipStates: [],
    };
    record.presence = presence;
    records.set(id, record);
  }

  return [...records.entries()]
    .map(([id, record]) => ({
      id,
      tenantId: record.tenantId,
      alias: record.alias,
      roomIds: [...record.roomIds].sort((left, right) => left.localeCompare(right)),
      roomMembership: Object.fromEntries(record.roomMembership),
      membershipEnabled: record.membershipStates.length
        ? record.membershipStates.some(Boolean)
        : undefined,
      presence: record.presence,
      leaseState: leaseState(leaseExpiry(record.presence ?? {})),
    }))
    .sort((left, right) => {
      const stateRank = { online: 0, unknown: 1, expired: 2 };
      return stateRank[left.leaseState] - stateRank[right.leaseState]
        || left.tenantId.localeCompare(right.tenantId)
        || left.alias.localeCompare(right.alias);
    });
}

/** Live state of the agent; without an activity row it falls back to the lease alone. */
export function liveStateOf(agent: Pick<FleetAgent, 'leaseState'>, view: Pick<LiveAgentView, 'state'> | undefined): LiveState {
  return view?.state ?? (agent.leaseState === 'online' ? 'idle' : 'down');
}

export function agentLiveState(agent: FleetAgent, live: ReadonlyMap<string, LiveAgentView> | undefined): LiveState {
  return liveStateOf(agent, live?.get(agent.id));
}

/** Explicit PTY states. There is no implicit "available": absent data is UNKNOWN. */
export type TerminalAccessStatus = 'allowed' | 'denied' | 'offline' | 'unknown';

export interface TerminalTargetResolution {
  status: TerminalAccessStatus;
  /** Always populated: every disabled control must be able to say why. */
  reason: string;
  target?: TerminalTarget;
}

export const TERMINAL_ACCESS_LABELS: Readonly<Record<TerminalAccessStatus, string>> = {
  allowed: 'Terminal conectada',
  denied: 'Sin autoridad',
  offline: 'Agente de terminal desconectado',
  unknown: 'Conexión sin comprobar',
};

/** Resolves a destination by exact tenant:alias identity; a bare alias never matches. */
export function terminalTargetForAgent(targets: TerminalTarget[] | null | undefined, agent: FleetAgent): TerminalTarget | undefined {
  return (targets ?? []).find((target) => fleetAgentId(target.tenant_id, target.alias) === agent.id);
}

/**
 * Per-destination gate. The server's `authorized` flag is the authority; the client only
 * translates it, and every path that is not an explicit allow stays closed with its motive.
 */
export function resolveTerminalTarget(targets: TerminalTarget[] | null | undefined, agent: FleetAgent): TerminalTargetResolution {
  if (!targets) {
    return { status: 'unknown', reason: 'El servidor no publicó la lista de destinos de terminal, así que no se sabe si este agente tiene terminal. No se asume que sí.' };
  }
  const target = terminalTargetForAgent(targets, agent);
  if (!target) {
    return { status: 'unknown', reason: `El servidor no declaró un destino de terminal para ${agent.tenantId}:${agent.alias}.` };
  }
  if (!target.authorized) return { status: 'denied', reason: target.reason, target };
  // Rolling upgrades can briefly pair a new console with an old gateway which used `ok` for
  // every authorized row, even when its measured PTY state was unusable. Never surface that
  // authority placeholder as the explanation on a disabled control.
  const placeholderReason = target.reason.trim().length === 0 || /^\s*ok\.?\s*$/iu.test(target.reason);
  if (target.pty_state === 'not_installed') {
    return {
      status: 'unknown',
      reason: 'Presencia no observada del agente de terminal para este destino; conexión e instalación quedan sin comprobar.',
      target,
    };
  }
  if (target.pty_state === 'unknown') {
    return {
      status: 'unknown',
      reason: placeholderReason
        ? 'No se observó presencia del agente de terminal para este destino; conexión e instalación quedan sin comprobar.'
        : `${target.reason} Presencia no observada: conexión e instalación quedan sin comprobar.`,
      target,
    };
  }
  if (target.pty_state === 'agent_offline') {
    return {
      status: 'offline',
      reason: `${placeholderReason
        ? 'El agente de terminal figura fuera de línea: no está conectado al servicio de terminales.'
        : target.reason} Última presencia: ${target.last_seen ?? 'nunca se registró una'}.`,
      target,
    };
  }
  return { status: 'allowed', reason: target.reason, target };
}

/** How many destinations the server reports as reachable. UNKNOWN inventory stays UNKNOWN. */
export function countOnlinePtyTargets(targets: TerminalTarget[] | null | undefined): number | undefined {
  return targets ? targets.filter((target) => target.authorized && target.pty_state === 'online').length : undefined;
}

/* -------------------------------------------------------------------------- */
/* Live TUI                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * The PTY agent's `harness` mode connects to the agent's active TUI (tmux session).
 * If HARNESS_COMMAND is not set or there is no grant, it degrades to shell mode.
 */
export const LIVE_TUI_MODE = 'harness';

/** Fresh shell inside the agent's container. */
export const SHELL_MODE = 'shell';

/** Writable TUI: the SAME tmux the agent is drawing, with the keyboard attached to it. */
export const WRITABLE_TUI_MODE = 'harness_rw';

/** Writable modes as PUBLISHED by the gateway. An older gateway publishes none, which is a no. */
function modosEscribiblesPublicados(target: TerminalTarget | undefined): string[] {
  return target?.writable_modes ?? [];
}

/** Does this destination offer the writable TUI? Never inferred from the mode list. */
export function ofreceTuiEscribible(target: TerminalTarget | undefined): boolean {
  return modosEscribiblesPublicados(target).includes(WRITABLE_TUI_MODE);
}

/**
 * Read-only is a function of the MODE and of the CONTROL, not of the mode alone: `harness` never
 * writes, `harness_rw` writes only while this browser holds the control, and `shell` is its own.
 */
export function terminalEsSoloLectura(mode: string | undefined, controlSostenido: boolean): boolean {
  if (mode === LIVE_TUI_MODE) return true;
  if (mode === WRITABLE_TUI_MODE) return !controlSostenido;
  return false;
}

export type LiveTuiStatus = 'available' | 'no_tui' | 'blocked' | 'unknown';

interface LiveTuiResolution {
  status: LiveTuiStatus;
  /** Always populated: a grey button without a reason is the same as saying nothing. */
  reason: string;
  target?: TerminalTarget;
}

export const LIVE_TUI_LABELS: Readonly<Record<LiveTuiStatus, string>> = {
  available: 'TUI en vivo',
  no_tui: 'TUI no disponible',
  blocked: 'TUI bloqueada',
  unknown: 'TUI desconocida',
};

/**
 * Can this alias emit its TUI? It leans on the same per-destination authority as the rest of the
 * PTY plane and adds just one question: did the server publish the `harness` mode for it?
 */
export function resolveLiveTui(targets: TerminalTarget[] | null | undefined, agent: FleetAgent): LiveTuiResolution {
  const base = resolveTerminalTarget(targets, agent);
  if (base.status !== 'allowed') {
    return {
      status: base.status === 'unknown' ? 'unknown' : 'blocked',
      reason: base.reason,
      ...(base.target ? { target: base.target } : {}),
    };
  }
  const target = base.target;
  if (!target) return { status: 'unknown', reason: base.reason };
  if (target.modes.includes(LIVE_TUI_MODE)) {
    return { status: 'available', reason: 'El servidor publica el modo harness: hay TUI en vivo para este alias.', target };
  }
  return {
    status: 'no_tui',
    reason: `El agente de terminal de ${agent.alias} no publica el modo ${LIVE_TUI_MODE}: su arnés puede no ofrecer una TUI o su sesión aún no está preparada. Puedes abrir Shell. Modos publicados: ${target.modes.length ? target.modes.join(', ') : 'ninguno'}.`,
    target,
  };
}

/** How many destinations can emit their TUI. Absent inventory remains UNKNOWN. */
export function countLiveTuiTargets(targets: TerminalTarget[] | null | undefined): number | undefined {
  return targets
    ? targets.filter((target) => target.authorized && target.pty_state === 'online' && target.modes.includes(LIVE_TUI_MODE)).length
    : undefined;
}
